<template>
  <div class="modal fade" id="prepareNewSemesterModalForm" tabindex="-1" aria-labelledby="prepareNewSemesterModalFormLabel" aria-hidden="true">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header bg-warning">
          <h5 class="modal-title" id="prepareNewSemesterModalFormLabel">Prepare the application for a new semester</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <!--Warnings -->
          <div v-if="showWarning" class="alert alert-danger mb-0" role="alert">
            <p class="mb-2">
              This action will close the current semester and prepare the application for a new semester.
            </p>
            <ul class="mb-3">
              <li>All data from current semester won't be accessible in application after this action.</li>
              <li>You can choose whether to copy current global files, homeworks, and subtasks into the new semester.</li>
              <li>New semester will be created.</li>
              <li>This action can take a while.</li>
            </ul>
            <div class="d-flex justify-content-end gap-2">
              <button type="button" class="btn btn-secondary" @click="cancelModal">Cancel</button>
              <button type="button" class="btn btn-danger" @click="continueFromWarning">OK</button>
            </div>
          </div>

          <!-- Actual form -->
          <form v-else @submit.prevent="submitForm">
            <div class="mb-3">
              <label for="semester_name" class="form-label">Semester name</label>
              <input type="text" id="semester_name" class="form-control" v-model.trim="name" placeholder="Spring 2024" required>
            </div>
            <div class="mb-3">
              <label for="start_date" class="form-label">Start date</label>
              <input type="date" id="start_date" class="form-control" v-model="start_date" required>
            </div>
            <div class="mb-3">
              <label for="end_date" class="form-label">End date</label>
              <input
                type="date"
                id="end_date"
                class="form-control"
                v-model="end_date"
                required
              >
              <div class="form-text text-danger" v-if="!isDateOrderValid">
                End date must be after start date.
              </div>
            </div>
            <hr>
            <div class="mb-3 form-check">
              <input type="checkbox" id="copyResources" class="form-check-input"
                v-model="copy_resources_from_last_semester"
                aria-describedby="copyResourcesHelp copyResourcesWarn"
              >
              <label for="copyResources" class="form-check-label">
                Copy global files, homeworks, and subtasks from the current semester
              </label>
              <div id="copyResourcesHelp" class="form-text">
                If enabled, reusable course resources will be copied into the new semester.
              </div>
            </div>
            <hr>
            <div class="mb-3">
              <label for="deleteConfirm" class="form-label">To confirm, type: <strong>DELETE</strong></label>
              <input type="text" id="deleteConfirm" class="form-control" v-model.trim="deleteConfirmText" placeholder="DELETE" required>
              <div class="form-text text-danger" v-if="deleteConfirmText !== 'DELETE'">You cannot continue without confirmation.</div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" :disabled="submitting">Close</button>
              <button type="submit" class="btn btn-danger" :disabled="!canSubmit || submitting">
                <span v-if="submitting" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Confirm and prepare
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>

  <StemErrorModal ref="stemErrorModal" />
</template>

<script>
import { Modal } from 'bootstrap';
import StemErrorModal from '@/components/StemErrorModal.vue';

export default {
  name: 'PrepareNewSemesterModalForm',
  components: { StemErrorModal },
  data() {
    return {
      showWarning: true,
      name: '',
      start_date: '',
      end_date: '',
      copy_resources_from_last_semester: true,
      deleteConfirmText: '',
      submitting: false,
    };
  },
  computed: {
    canSubmit() {
      return (
        this.deleteConfirmText === 'DELETE' && this.isDateOrderValid
      );
    },
    isDateOrderValid() {
      if (!this.start_date || !this.end_date) return true;
      const start = new Date(this.start_date);
      const end = new Date(this.end_date);
      if (isNaN(start.getTime()) || isNaN(end.getTime())) return true;
      return start < end;
    },
  },
  mounted() {
    const modalElement = document.getElementById('prepareNewSemesterModalForm');
    modalElement.addEventListener('hidden.bs.modal', this.resetForm);
  },
  methods: {
    resetForm() {
      this.showWarning = true;
      this.name = '';
      this.start_date = '';
      this.end_date = '';
      this.copy_resources_from_last_semester = true;
      this.deleteConfirmText = '';
      this.submitting = false;
    },
    continueFromWarning() {
      this.showWarning = false;
    },
    cancelModal() {
      const modalElement = document.getElementById('prepareNewSemesterModalForm');
      const modalInstance = Modal.getInstance(modalElement) || new Modal(modalElement);
      if (modalInstance) modalInstance.hide();
    },
    async submitForm() {
      try {
        if (!this.canSubmit) return;
        if (!this.isDateOrderValid) {
          this.$refs.stemErrorModal.show('Invalid dates', 'Start date must be before end date.');
          return;
        }
        this.submitting = true;
        // Send payload using parent handler
        this.$emit('submitForm', {
          name: this.name,
          start_date: this.start_date,
          end_date: this.end_date,
          copy_resources_from_last_semester: this.copy_resources_from_last_semester,
          archived: false,
        });

        // Hide modal after triggering the action
        const modalElement = document.getElementById('prepareNewSemesterModalForm');
        const modalInstance = Modal.getInstance(modalElement);
        if (modalInstance) modalInstance.hide();
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>
